import { AssessmentPrompt, DeletePromptInput } from '@amzn/aws-assessment-template-management-service-typescript-client';
import { Box, Button, Modal, SpaceBetween } from '@amzn/awsui-components-react';
import { useMutation } from '@apollo/client';
import dompurify from 'dompurify';
import { FunctionComponent, useCallback } from 'react';
import { v4 as uuid } from 'uuid';

import templateManagementClient from '../../../../../api/templateManagement/TemplateManagementClient';
import { DELETE_PROMPT } from '../../../../../api/templateManagement/TemplateManagementMutations';
import { AppLabelsContextInterface, withAppLabelsContext } from '../../../../../common/AppLabelsContext';
import rumClient from '../../../../../common/monitoring/RumClient';
import { deleteCurrentTemplatePrompt, removeInProgressRequest } from '../../../../administration/manage-templates/edit-template/CurrentTemplateSlice';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';

type DeleteQuestionModalProps = AppLabelsContextInterface & {
    visible: boolean;
    setVisible: (visible: boolean) => void;
};

const DeleteQuestionModal: FunctionComponent<DeleteQuestionModalProps> = ({ appLabels, visible, setVisible }) => {
    const dispatch = useAppDispatch();

    const currentModuleId = useAppSelector((state) => state.currentTemplateState.currentPromptState.currentModuleId);
    const currentPrompt: AssessmentPrompt | undefined = useAppSelector((state) => state.currentTemplateState.currentPromptState.currentPrompt);

    const [deletePromptMutation, { loading }] = useMutation(DELETE_PROMPT, {
        client: templateManagementClient,
        onError: (error, options) => {
            rumClient.recordError(error);
            dispatch(removeInProgressRequest({ requestId: options.context.requestId, didRequestFail: true }));
        },
        onCompleted: (_, options) => {
            dispatch(deleteCurrentTemplatePrompt());
            dispatch(removeInProgressRequest({ requestId: options.context.requestId }));
        },
    });

    const onConfirm = useCallback(async () => {
        try {
            const deletePromptInput: DeletePromptInput = {
                moduleId: currentModuleId,
                promptId: currentPrompt?.promptId,
            };

            const requestId = uuid();
            await deletePromptMutation({ variables: { input: deletePromptInput }, context: { requestId } });
        } finally {
            setVisible(false);
        }
    }, [currentModuleId, currentPrompt?.promptId, deletePromptMutation, setVisible]);

    // Render HTML app label
    const deleteQuestionWarning = dompurify.sanitize(appLabels.manage_templates.edit.modify_structure.delete_question_warning);

    return (
        <Modal
            data-testid='delete-question-modal'
            onDismiss={() => setVisible(false)}
            footer={
                <Box float='right'>
                    <SpaceBetween direction='horizontal' size='xs'>
                        <Button variant='link' disabled={loading} onClick={() => setVisible(false)}>
                            {appLabels.user_actions.cancel}
                        </Button>
                        <Button variant='primary' loading={loading} onClick={onConfirm} data-testid='btn-confirm-delete-question'>
                            {appLabels.user_actions.confirm}
                        </Button>
                    </SpaceBetween>
                </Box>
            }
            header={appLabels.manage_templates.edit.modify_structure.delete_question}
            visible={visible}
        >
            <div dangerouslySetInnerHTML={{ __html: deleteQuestionWarning }} />
        </Modal>
    );
};

export default withAppLabelsContext(DeleteQuestionModal);
